import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, CheckCircle, LinkSimple, EnvelopeSimple, ChatCircleDots, WhatsappLogo, PaperPlaneTilt, FilePdf } from "@phosphor-icons/react";
import { openContractPdfPreview } from "@/lib/pdfPreview";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ShareDialog({ contractId, open, onOpenChange }) {
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("Please review the attached contract.");
  const [sending, setSending] = useState(false);
  const [lastDelivery, setLastDelivery] = useState(null);

  React.useEffect(() => {
    if (open && contractId) {
      api.post(`/contracts/${contractId}/share`, { channel: "link", base_url: window.location.origin })
        .then(({ data }) => setShare(data));
    } else {
      setShare(null); setLastDelivery(null);
    }
  }, [open, contractId]);

  const publicUrl = share?.url || (share ? `${window.location.origin}/briefing/${share.token}` : "");

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const sendVia = async (channel) => {
    if (!recipient.trim()) {
      toast.error(channel === "email" ? "Enter recipient email" : "Enter phone number");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/contracts/${contractId}/share`, {
        channel, recipient: recipient.trim(), message,
        base_url: window.location.origin,
      });
      setLastDelivery(data.delivery);
      const s = data.delivery?.status;
      if (s === "sent") toast.success(`Delivered via ${channel}`);
      else if (s === "mocked") toast.info(`Mocked ${channel} — configure ${channel === "email" ? "Resend" : "Twilio"} in Integrations to deliver for real`);
      else toast.warning(`${channel} delivery status: ${s || "unknown"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delivery failed");
    } finally { setSending(false); }
  };

  const previewPdf = () => openContractPdfPreview(contractId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-0 max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkSimple size={18} weight="duotone" className="text-[var(--accent)]" /> Share draft contract
          </DialogTitle>
        </DialogHeader>
        {share ? (
          <div className="space-y-4 mt-2">
            <Tabs defaultValue="link">
              <TabsList data-testid="share-tabs" className="grid grid-cols-4">
                <TabsTrigger value="link" data-testid="share-tab-link"><LinkSimple size={12} className="mr-1" /> Link</TabsTrigger>
                <TabsTrigger value="email" data-testid="share-tab-email"><EnvelopeSimple size={12} className="mr-1" /> Email</TabsTrigger>
                <TabsTrigger value="sms" data-testid="share-tab-sms"><ChatCircleDots size={12} className="mr-1" /> SMS</TabsTrigger>
                <TabsTrigger value="whatsapp" data-testid="share-tab-whatsapp"><WhatsappLogo size={12} className="mr-1" /> WhatsApp</TabsTrigger>
              </TabsList>

              <TabsContent value="link" className="space-y-3 pt-4">
                <p className="text-xs text-[var(--text-2)]">Anyone with this link can view the AI briefing and sign. Views are tracked.</p>
                <div className="bg-white p-3 rounded-md flex justify-center">
                  <QRCodeSVG value={publicUrl} size={140} level="H" includeMargin />
                </div>
                <div className="flex gap-2">
                  <Input value={publicUrl} readOnly data-testid="share-url-input" className="font-mono text-xs glass-subtle border-[var(--border)]" />
                  <Button onClick={copy} data-testid="share-copy-btn" className="gap-2 shrink-0">
                    {copied ? <CheckCircle size={14} weight="fill" /> : <Copy size={14} />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <Button variant="outline" onClick={previewPdf} data-testid="share-preview-pdf-btn" className="w-full gap-2">
                  <FilePdf size={14} weight="duotone" /> Preview as PDF
                </Button>
              </TabsContent>

              {["email", "sms", "whatsapp"].map((ch) => (
                <TabsContent key={ch} value={ch} className="space-y-3 pt-4">
                  <Input
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder={ch === "email" ? "recipient@example.com" : "+14155550123"}
                    data-testid={`share-recipient-${ch}`}
                    className="glass-subtle border-[var(--border)]"
                  />
                  <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid={`share-message-${ch}`} className="glass-subtle border-[var(--border)] text-sm" />
                  <Button onClick={() => sendVia(ch)} disabled={sending} data-testid={`share-send-${ch}`} className="w-full gap-2">
                    <PaperPlaneTilt size={14} /> {sending ? "Sending…" : `Send via ${ch}`}
                  </Button>
                  {lastDelivery && (
                    <div className="text-[11px] text-[var(--text-3)] p-2 rounded bg-[var(--surface)] border border-[var(--border)]">
                      <div><b>Status:</b> {lastDelivery.status}</div>
                      {lastDelivery.reason && <div className="mt-1">{lastDelivery.reason}</div>}
                      {lastDelivery.source && <div className="mt-0.5 opacity-70">source: {lastDelivery.source}</div>}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>

            <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-[var(--border)]">
              <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                <div className="text-lg font-semibold font-mono">{share.views || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mt-0.5">Views</div>
              </div>
              <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                <div className="text-lg font-semibold font-mono">{share.signed_at ? "✓" : "—"}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mt-0.5">Signed</div>
              </div>
              <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                <div className="text-lg font-semibold font-mono">∞</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mt-0.5">Expiry</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-[var(--text-3)]">Generating share link…</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
